import { useQuery } from '@tanstack/react-query';

import { AxiosCartError, Cart } from '@telialt/models/src/EshopModel';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import {
    PARAM_DISNEY,
    PARAM_GIFTS,
    PARAM_GUID_QUERY,
    PARAM_ID_QUERY,
    PARAM_NETFLIX,
    PARAM_SERVICE_SO_ID_QUERY,
    PARAM_SERVICE_UO_ID_QUERY,
    PARAM_SERVICE_UO_TYPE_QUERY,
    PARAM_TYPE_QUERY,
} from '@telialt/utils/src/eshopConstants';
import { getCartIdSync, getCartSession, isCartLoadedFromUrl } from '@telialt/utils/src/eshopUtils';
import QueryString from '@telialt/utils/src/QueryString';
import { useSegment } from '@telialt/utils/src/segment';
import { getIsUserAuthorized } from '@telialt/utils/src/SSO/utils';

import { ESHOP_QUERY_KEY } from '../../model';
import { apiGetCart, apiMigrateCart } from '../../store/api';

export const useStrategyLoadCartQuery = () => {
    const segment = useSegment();

    const query = useQuery<Cart, AxiosCartError>({
        queryKey: [ESHOP_QUERY_KEY.CART],
        queryFn: async ({ signal }) => {
            const { search } = window.location;
            const type = new QueryString(search).getParam(PARAM_TYPE_QUERY);
            const serviceUoId = new QueryString(search).getParam(PARAM_SERVICE_UO_ID_QUERY);
            const serviceSoId = new QueryString(search).getParam(PARAM_SERVICE_SO_ID_QUERY);
            const serviceUoType = new QueryString(search).getParam(PARAM_SERVICE_UO_TYPE_QUERY);
            const gifts = new QueryString(search).getParam(PARAM_GIFTS);
            const disney = new QueryString(search).getParam(PARAM_DISNEY);
            const netflix = new QueryString(search).getParam(PARAM_NETFLIX);
            const cartSession = getCartSession();

            if (type && serviceUoId) {
                return (
                    await apiMigrateCart(segment, {
                        type,
                        serviceUoId,
                        serviceSoId,
                        UOType: serviceUoType,
                        gifts,
                        disney,
                        netflix,
                    })
                ).data;
            }

            if (isCartLoadedFromUrl(search)) {
                const queryString = new QueryString(search);
                const guid = queryString.getParam(PARAM_GUID_QUERY);
                const code = queryString.getParam(PARAM_ID_QUERY);

                return (await apiGetCart(segment, { cartId: guid || code })).data;
            }

            if (cartSession == null) {
                throw Error('cart session is empty, defaulting to "errorPlaceholder"');
            }

            if (cartSession?.isCartAnonymous && getIsUserAuthorized()) {
                return (await apiMigrateCart(segment, { oldCartId: cartSession.guid })).data;
            }

            const cartIdFromSession = getCartIdSync(cartSession);

            return (await apiGetCart(segment, { cartId: cartIdFromSession }, null, signal)).data;
        },
        staleTime: 3000,
        retryOnMount: false,
        refetchOnMount: true,
    });

    useLogError(query.error, DDComponent.Eshop);

    return query;
};
